/**
 * spreadsheetStores.js
 * All Zustand stores for the enterprise spreadsheet platform.
 * Separate stores by concern; compose in components via hooks.
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { enableMapSet } from 'immer';
import { immer } from 'zustand/middleware/immer';

enableMapSet();

// ─────────────────────────────────────────────────────────────────────────────
// 1. WORKBOOK STORE — top-level workbook state
// ─────────────────────────────────────────────────────────────────────────────
export const useWorkbookStore = create(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        workbookId:   null,
        workbookName: '',
        fiscalYear:   '',
        lastSaved:    null,
        saveStatus:   'saved',   // 'saved' | 'unsaved' | 'saving' | 'error'
        isSaving:     false,
        isLoading:    false,
        error:        null,

        // Sheet registry: [{ sheetId, sheetName }]
        sheets: [],
        activeSheetId: null,

        // ── Actions ────────────────────────────────────────────────────────
        setWorkbook(meta) {
          set((s) => {
            s.workbookId   = meta.workbookId;
            s.workbookName = meta.workbookName;
            s.fiscalYear   = meta.fiscalYear   || '';
            s.sheets       = meta.sheets.map((sh) => ({ sheetId: sh.sheetId, sheetName: sh.sheetName }));
            s.activeSheetId = meta.sheets[0]?.sheetId || null;
            s.lastSaved    = meta.lastSaved || null;
          });
        },

        setActiveSheet(sheetId) {
          set((s) => { s.activeSheetId = sheetId; });
        },

        setSaveStatus(status) {
          set((s) => { s.saveStatus = status; s.isSaving = status === 'saving'; });
        },

        setLastSaved(ts) {
          set((s) => { s.lastSaved = ts; s.saveStatus = 'saved'; });
        },

        setLoading(isLoading) {
          set((s) => { s.isLoading = isLoading; });
        },

        setError(error) {
          set((s) => { s.error = error; });
        },

        addSheet(sheet) {
          set((s) => { s.sheets.push({ sheetId: sheet.sheetId, sheetName: sheet.sheetName }); });
        },

        markUnsaved() {
          set((s) => { if (s.saveStatus === 'saved') s.saveStatus = 'unsaved'; });
        },
      }))
    ),
    { name: 'WorkbookStore' }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// 2. SHEET STORE — per-sheet row data and dirty tracking
// ─────────────────────────────────────────────────────────────────────────────
export const useSheetStore = create(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        // { sheetId: rowData[] }
        rowDataMap:    {},
        // { sheetId: Set<rowId> }
        dirtyRowsMap:  {},
        // { sheetId: { fieldName: errorMsg }[] }  (indexed by row index)
        errorMap:      {},

        // ── Actions ────────────────────────────────────────────────────────

        setSheetData(sheetId, rows) {
          set((s) => {
            s.rowDataMap[sheetId]   = rows;
            s.dirtyRowsMap[sheetId] = new Set();
          });
        },

        updateCell(sheetId, rowId, field, value) {
          set((s) => {
            const rows = s.rowDataMap[sheetId];
            if (!rows) return;
            const idx = rows.findIndex((r) => r.id === rowId);
            if (idx === -1) return;
            rows[idx][field] = value;
            if (!s.dirtyRowsMap[sheetId]) s.dirtyRowsMap[sheetId] = new Set();
            s.dirtyRowsMap[sheetId].add(rowId);
          });
        },

        updateComputedCells(sheetId, changedCells, columns) {
          /**
           * changedCells: [{ row, col, value }] from HyperFormula
           * col is zero-based index into columns array
           */
          set((s) => {
            const rows = s.rowDataMap[sheetId];
            if (!rows || !columns) return;
            changedCells.forEach(({ row, col, value }) => {
              const field = columns[col]?.field;
              if (!field || !rows[row]) return;
              rows[row][field] = value;
            });
          });
        },

        addRow(sheetId, newRow) {
          set((s) => {
            if (!s.rowDataMap[sheetId]) s.rowDataMap[sheetId] = [];
            s.rowDataMap[sheetId].push(newRow);
            if (!s.dirtyRowsMap[sheetId]) s.dirtyRowsMap[sheetId] = new Set();
            s.dirtyRowsMap[sheetId].add(newRow.id);
          });
        },

        deleteRows(sheetId, rowIds) {
          set((s) => {
            if (!s.rowDataMap[sheetId]) return;
            s.rowDataMap[sheetId] = s.rowDataMap[sheetId].filter((r) => !rowIds.includes(r.id));
          });
        },

        clearDirty(sheetId) {
          set((s) => { s.dirtyRowsMap[sheetId] = new Set(); });
        },

        setRowErrors(sheetId, rowIndex, errors) {
          set((s) => {
            if (!s.errorMap[sheetId]) s.errorMap[sheetId] = {};
            s.errorMap[sheetId][rowIndex] = errors;
          });
        },

        clearErrors(sheetId) {
          set((s) => { delete s.errorMap[sheetId]; });
        },

        // ── Selectors ──────────────────────────────────────────────────────

        getDirtyRows(sheetId) {
          const dirty = get().dirtyRowsMap[sheetId] || new Set();
          const rows  = get().rowDataMap[sheetId]   || [];
          return rows.filter((r) => dirty.has(r.id));
        },

        getDirtyCount(sheetId) {
          return (get().dirtyRowsMap[sheetId] || new Set()).size;
        },

        getTotalErrorCount(sheetId) {
          const em = get().errorMap[sheetId] || {};
          return Object.values(em).reduce((sum, e) => sum + Object.keys(e).length, 0);
        },
      }))
    ),
    { name: 'SheetStore' }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// 3. FORMULA STORE — formula bar and active cell state
// ─────────────────────────────────────────────────────────────────────────────
export const useFormulaStore = create(
  devtools(
    immer((set) => ({
      // The formula/value currently shown in the formula bar
      formulaBarValue:  '',
      // Whether the formula bar is in edit mode
      isEditing:        false,
      // Active cell address (e.g. "B4")
      activeCellAddress:'',
      // The resolved formula string for the active cell (if it's a formula)
      activeCellFormula: null,
      // Worker initialization status
      workerReady:      false,

      setFormulaBarValue(value) {
        set((s) => { s.formulaBarValue = value; });
      },

      setActiveCellAddress(address) {
        set((s) => { s.activeCellAddress = address; });
      },

      setActiveCellFormula(formula) {
        set((s) => { s.activeCellFormula = formula; s.formulaBarValue = formula || ''; });
      },

      setEditing(isEditing) {
        set((s) => { s.isEditing = isEditing; });
      },

      setWorkerReady(ready) {
        set((s) => { s.workerReady = ready; });
      },

      clearActiveCell() {
        set((s) => { s.activeCellAddress = ''; s.activeCellFormula = null; s.formulaBarValue = ''; });
      },
    })),
    { name: 'FormulaStore' }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// 4. METADATA STORE — workbook and sheet metadata
// ─────────────────────────────────────────────────────────────────────────────
export const useMetadataStore = create(
  devtools(
    immer((set, get) => ({
      // { workbookId: workbookMetadata }
      workbookMetaMap: {},

      setWorkbookMeta(workbookId, meta) {
        set((s) => { s.workbookMetaMap[workbookId] = meta; });
      },

      getSheetMeta(workbookId, sheetId) {
        const wb = get().workbookMetaMap[workbookId];
        return wb?.sheets.find((s) => s.sheetId === sheetId) || null;
      },

      getColumns(workbookId, sheetId) {
        const sheet = get().getSheetMeta(workbookId, sheetId);
        return sheet?.columns || [];
      },

      getPermissions(workbookId, sheetId) {
        const sheet = get().getSheetMeta(workbookId, sheetId);
        return sheet?.effectivePermissions || {};
      },

      getToolbarActions(workbookId, sheetId) {
        const sheet = get().getSheetMeta(workbookId, sheetId);
        return sheet?.toolbarActions || [];
      },
    })),
    { name: 'MetadataStore' }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// 5. UI STORE — transient UI state (toasts, search, selection)
// ─────────────────────────────────────────────────────────────────────────────
export const useUiStore = create(
  devtools(
    immer((set, get) => ({
      toasts:         [],
      searchText:     '',
      selectedRowIds: [],
      theme:          'light', // 'light' | 'dark'
      isFullscreen:   false,
      sidePanelOpen:  false,
      sidePanelContent: null,

      // ── Toast ──────────────────────────────────────────────────────────
      addToast(message, type = 'info') {
        const id = Date.now() + Math.random();
        set((s) => { s.toasts.push({ id, message, type }); });
        setTimeout(() => {
          set((s) => { s.toasts = s.toasts.filter((t) => t.id !== id); });
        }, 4500);
        return id;
      },

      removeToast(id) {
        set((s) => { s.toasts = s.toasts.filter((t) => t.id !== id); });
      },

      // ── Search ─────────────────────────────────────────────────────────
      setSearchText(text) {
        set((s) => { s.searchText = text; });
      },

      // ── Selection ──────────────────────────────────────────────────────
      setSelectedRowIds(ids) {
        set((s) => { s.selectedRowIds = ids; });
      },

      // ── Theme ──────────────────────────────────────────────────────────
      toggleTheme() {
        set((s) => { s.theme = s.theme === 'light' ? 'dark' : 'light'; });
      },

      // ── Fullscreen ─────────────────────────────────────────────────────
      toggleFullscreen() {
        set((s) => { s.isFullscreen = !s.isFullscreen; });
      },

      // ── Side panel ─────────────────────────────────────────────────────
      openSidePanel(content) {
        set((s) => { s.sidePanelOpen = true; s.sidePanelContent = content; });
      },

      closeSidePanel() {
        set((s) => { s.sidePanelOpen = false; s.sidePanelContent = null; });
      },
    })),
    { name: 'UiStore' }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: aggregate dirty count across all sheets
// ─────────────────────────────────────────────────────────────────────────────
export function useTotalDirtyCount() {
  const dirtyRowsMap = useSheetStore((s) => s.dirtyRowsMap);
  return Object.values(dirtyRowsMap).reduce((sum, s) => sum + (s?.size || 0), 0);
}
